<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $t('messages.level_required'),
                        href: null,
                    },
                    {
                        name: subject.name,
                        href: null,
                    },
                ]"
            />
        </template>

        <PageSectionCollapsible class="mt-5" is-open>
            <template v-slot:title>
                <h2>{{ $tc('messages.content_blocks', 2) }}</h2>
            </template>

            <form :action="meta.links.update" @submit.prevent="submit">
                <div class="mt-5">
                    <div class="bg-gray-100 overflow-hidden rounded-lg mt-3">
                        <div class="px-4 py-5 sm:p-6">
                            <div class="flex flex-col mt-5">
                                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                                    <div
                                        class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                                    >
                                        <table class="min-w-full divide-y divide-gray-200">
                                            <thead>
                                                <tr class="text-white">
                                                    <th
                                                        class="max-w-xs px-6 py-3 bg-gray-400 text-left text-xs leading-4 font-medium tracking-wider"
                                                    >
                                                        {{ $tc('messages.tags', 2) }}
                                                    </th>
                                                    <th
                                                        class="max-w-xs px-6 py-3 bg-gray-400 text-left text-xs leading-4 font-medium tracking-wider"
                                                        v-for="gradeLevel in gradeLevels"
                                                        :key="gradeLevel.id"
                                                    >
                                                        {{ gradeLevel.name }}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <template v-for="block in form.blocks" :key="block.id">
                                                <thead>
                                                    <tr>
                                                        <th
                                                            class="max-w-xs px-6 py-3 bg-gray-300 text-left text-xs leading-4 font-medium text-gray-500 tracking-wider"
                                                        >
                                                            {{ block.name }}
                                                        </th>
                                                        <th
                                                            v-for="gradeLevel in gradeLevels"
                                                            :key="gradeLevel.id"
                                                            class="bg-gray-300"
                                                        >
                                                            <Tippy interactive>
                                                                <button type="button">
                                                                    <IconDotsVertical class="w-4 h-4 text-gray-400" />
                                                                </button>
                                                                <template #content>
                                                                    <div>
                                                                        {{ block.name }}
                                                                    </div>
                                                                    <div>
                                                                        {{ gradeLevel.name }}
                                                                    </div>
                                                                    <div>
                                                                        {{ $t('messages.set_the_same_value_for_all?') }}
                                                                    </div>
                                                                    <div class="flex flex-col gap-2">
                                                                        <div v-for="i in [1, 2, 3, 4, 5]" :key="i">
                                                                            <AppButton
                                                                                element="button"
                                                                                type="button"
                                                                                class="font-mono"
                                                                                @click="
                                                                                    setTagDifficulty(
                                                                                        block,
                                                                                        gradeLevel,
                                                                                        i,
                                                                                    )
                                                                                "
                                                                            >
                                                                                {{ i }}
                                                                            </AppButton>
                                                                        </div>
                                                                        <div>
                                                                            <AppButton
                                                                                element="button"
                                                                                type="button"
                                                                                @click="
                                                                                    setTagDifficulty(
                                                                                        block,
                                                                                        gradeLevel,
                                                                                        null,
                                                                                    )
                                                                                "
                                                                            >
                                                                                {{ $t('messages.empty_value') }}
                                                                            </AppButton>
                                                                        </div>
                                                                    </div>
                                                                </template>
                                                            </Tippy>
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody v-for="section in block.sections" :key="section.name">
                                                    <tr>
                                                        <td
                                                            :colspan="3 + gradeLevels.length"
                                                            class="max-w-xs px-6 py-3 bg-gray-200 text-left text-xs leading-4 font-medium text-gray-500 tracking-wider"
                                                        >
                                                            {{ section.name }}
                                                        </td>
                                                    </tr>
                                                    <tr v-for="tag in section.tags" :key="tag.id" class="group">
                                                        <td
                                                            class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex gap-2"
                                                        >
                                                            <span>{{ tag.name }}</span>
                                                        </td>
                                                        <td
                                                            class="px-6 py-4 whitespace-nowrap leading-5 text-center"
                                                            v-for="expectedLevel in tag.expectedLevels"
                                                            :key="expectedLevel.grade_level_id"
                                                            :class="{
                                                                'bg-red-400': expectedLevel.level === 1,
                                                                'bg-orange-400': expectedLevel.level === 2,
                                                                'bg-yellow-400': expectedLevel.level === 3,
                                                                'bg-blue-400': expectedLevel.level === 4,
                                                                'bg-green-400': expectedLevel.level === 5,
                                                            }"
                                                        >
                                                            <FormSelect
                                                                :options="options.difficulties"
                                                                v-model.number="expectedLevel.level"
                                                                null-option
                                                                null-option-text=" "
                                                            />
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </template>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="flex justify-end mt-5">
                    <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
                </div>
            </form>
        </PageSectionCollapsible>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import PageSectionCollapsible from '../../../../Shared/PageSectionCollapsible.vue';
import SmartBreadcrumbs from '../../../../components/SmartBreadcrumbs.vue';
import FormSelect from '../../../../components/Forms/FormSelect.vue';
import SubmitFormButton from '../../Activities/Subactivities/SubmitFormButton.vue';
import { IconDotsVertical } from '@tabler/icons-vue';
import AppButton from '../../../../components/AppButton.vue';
import { Tippy } from 'vue-tippy';

export default {
    components: {
        Tippy,
        AppButton,
        IconDotsVertical,
        SubmitFormButton,
        FormSelect,
        SmartBreadcrumbs,
        PageSectionCollapsible,
        PanelPage,
    },
    layout: Layout,
    name: 'Edit',
    props: ['meta', 'subject', 'gradeLevels', 'blocks', 'options'],
    data() {
        return {
            form: this.$inertia.form({
                blocks: this.blocks,
            }),
        };
    },
    methods: {
        setTagDifficulty(block, gradeLevel, i) {
            console.log(block, gradeLevel, i);
            block.sections.forEach((section) => {
                section.tags.forEach((tag) => {
                    tag.expectedLevels.forEach((expectedLevel) => {
                        if (expectedLevel.grade_level_id === gradeLevel.id) {
                            expectedLevel.level = i;
                        }
                    });
                });
            });
        },
        submit() {
            this.form
                .transform((data) => {
                    console.log(data);
                    const tags = data.blocks
                        .flatMap((block) => block.sections.flatMap((section) => section.tags))
                        .map((t) => {
                            return {
                                id: t.id,
                                expectedLevels: t.expectedLevels.map((expectedLevel) => {
                                    return {
                                        level: expectedLevel.level,
                                        grade_level_id: expectedLevel.grade_level_id,
                                    };
                                }),
                            };
                        });
                    return {
                        tags,
                    };
                })
                .patch(this.meta.links.update, {
                    preserveScroll: true,
                });
        },
    },
};
</script>
